/**
 * Genera un WAV de silencio en memoria.
 *
 * ¿Por qué hace falta? `speechSynthesis` no crea una sesión de medios, así
 * que el sistema operativo no muestra controles en la pantalla de bloqueo ni
 * responde a los botones de los auriculares. El truco estándar es tener un
 * `<audio>` reproduciendo algo en bucle: con eso el navegador sí publica la
 * MediaSession y nuestros handlers reciben los eventos.
 *
 * Se construye en código en vez de incrustar un base64 opaco: 20 líneas
 * verificables por test valen más que una cadena mágica que nadie puede leer.
 *
 * Formato: PCM 16 bits, mono, 8 kHz (el mínimo razonable; solo es silencio).
 */

const SAMPLE_RATE = 8000
const BITS_PER_SAMPLE = 16
const CHANNELS = 1

/** Escribe una cadena ASCII en el buffer, byte a byte. */
function writeAscii(view: DataView, offset: number, text: string) {
  for (let i = 0; i < text.length; i++) {
    view.setUint8(offset + i, text.charCodeAt(i))
  }
}

/**
 * Devuelve un Blob `audio/wav` con `seconds` de silencio.
 * Por defecto 1 segundo: se reproduce en bucle (`loop`), así que basta.
 */
export function silentWavBlob(seconds = 1): Blob {
  const frames = Math.max(1, Math.round(SAMPLE_RATE * seconds))
  const bytesPerFrame = (BITS_PER_SAMPLE / 8) * CHANNELS
  const dataBytes = frames * bytesPerFrame
  const buffer = new ArrayBuffer(44 + dataBytes)
  const view = new DataView(buffer)

  writeAscii(view, 0, 'RIFF')
  view.setUint32(4, 36 + dataBytes, true) // tamaño total - 8
  writeAscii(view, 8, 'WAVE')
  writeAscii(view, 12, 'fmt ')
  view.setUint32(16, 16, true) // tamaño del bloque fmt
  view.setUint16(20, 1, true) // formato: PCM
  view.setUint16(22, CHANNELS, true)
  view.setUint32(24, SAMPLE_RATE, true)
  view.setUint32(28, SAMPLE_RATE * bytesPerFrame, true) // byte rate
  view.setUint16(32, bytesPerFrame, true) // block align
  view.setUint16(34, BITS_PER_SAMPLE, true)
  writeAscii(view, 36, 'data')
  view.setUint32(40, dataBytes, true)
  // Las muestras quedan en 0 = silencio (ArrayBuffer nace a ceros).

  return new Blob([buffer], { type: 'audio/wav' })
}
